import { motion } from 'motion/react';
import { PressableCard } from './ui/PressableCard';
import { formatMemberCount } from './utils/memberCount';
import { ArrowLeft, Search, Users, TrendingUp, Clock } from 'lucide-react';
import { Button } from './ui/button';
import logoImage from '../assets/a82ed08e29774a7ec97a8e2e8386d7838c141f5b.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import { ScrollArea } from './ui/scroll-area';
import { Input } from './ui/input';
import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { ParallaxBackground } from './utils/parallax';
import { CommunityFilterBar } from './CommunityFilterBar';
import { CommunityDiscoveryService, CommunityFilter } from '../services/CommunityDiscoveryService';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { Tribe } from '../types';
import { buildTrendingSet } from '../utils/communityTrending';
import { useScreenReady } from '../contexts/ScreenTransitionContext';

interface AllTribesScreenProps {
  onBack: () => void;
}

// Fallback static data removed to prevent flash of old content
const STATIC_TRIBES: any[] = [];

export function AllTribesScreen({ onBack }: AllTribesScreenProps) {
  const { userProfile } = useUserData();
  const { isMutedGroup } = useContentFilter();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<CommunityFilter>('trending');
  const [tribes, setTribes] = useState<any[]>([]);
  // Starts true: the fetch below runs on mount, and starting false meant the
  // first paint was an empty list claiming there were no tribes.
  const [loading, setLoading] = useState(true);

  useScreenReady(!loading);

  useEffect(() => {
    // Fetch real tribes
    const fetchTribes = async () => {
      setLoading(true);
      try {
        const results = await CommunityDiscoveryService.getCommunities(
          'tribes',
          activeFilter,
          userProfile?.userTags || [],
          20,
          isMutedGroup,
        );
        // Show real results only, no static fallback
        setTribes(results);
      } catch (e) {
        console.error("Failed to fetch tribes", e);
      } finally {
        setLoading(false);
      }
    };
    fetchTribes();
  }, [activeFilter, userProfile]);


  const filteredTribes = tribes.filter((tribe) => {
    const matchesSearch =
      (tribe.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (tribe.description?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
      (tribe.category?.toLowerCase() || '').includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Trending is scored from join rate, post rate and replies-per-post, then
  // awarded to the top slice of what's on screen (see communityTrending).
  const trendingIds = buildTrendingSet(filteredTribes as any[]);

  const handleTribeClick = (tribe: any) => {
    window.dispatchEvent(new CustomEvent('navigateToTribeGroup', { detail: tribe }));
  };

  return (
    // No pb-20 on the root, unlike the screens that sit behind the bottom
    // nav: /explore/tribes is not one of BOTTOM_NAV_PATHS, so that padding reserved
    // 80px of the viewport for a bar that never renders and the scrolling
    // list below was clipped 80px short of the bottom.
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-black/10 dark:border-white/10 bg-[#FDFBF7]/90 dark:bg-black/80 backdrop-blur-lg"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center gap-4 mb-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-black dark:hover:text-white"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div className="flex-1">
              <h1 className="text-2xl text-black dark:text-white font-serif">
                Tribes
              </h1>
              <p className="text-sm text-[#373737] dark:text-[#E5E5E5]">
                Find communities to yap, connect, and build with people who share your interests.
              </p>
            </div>
            {/* Top Right Logo */}
            <div className="relative">
              <div className="w-12 h-12 rounded-full p-[2px] bg-linear-to-br from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center">
                <img
                  src={String(logoImageWhite)}
                  alt="AfroYap"
                  className="w-8 h-8 object-contain"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 flex flex-col items-start px-2">
            <Button
              onClick={() => window.dispatchEvent(new CustomEvent('openCreateTribeModal'))}
              className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white shadow-lg mb-2"
            >
              <Users className="w-4 h-4 mr-2" />
              Establish a Tribe
            </Button>
            <p className="text-xs text-[#666666] dark:text-[#999999] pl-1">
              Costs 1000 Gold or uses a Tribe Credit.
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
            <Input
              placeholder="Search tribes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-white dark:bg-[#2a2a2a] border-none text-black dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.25)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)]"
            />
          </div>

          {/* Filter Bar */}
          <CommunityFilterBar
            selectedFilter={activeFilter}
            onFilterChange={setActiveFilter}
            className="mb-2"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pb-32">
          <div className="p-4 space-y-3">
            {filteredTribes.map((tribe, index) => (
              <PressableCard
                key={tribe.id || tribe.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => handleTribeClick(tribe)}
                className="relative rounded-2xl overflow-hidden cursor-pointer shadow-[0_15px_35px_rgba(0,0,0,0.2)] dark:shadow-[0_20px_40px_rgba(0,0,0,0.6)] group min-h-[120px] border-2 border-black/15 dark:border-white/30"
                style={{ backgroundColor: '#2a2a2a' }}
              >
                {/* Background Image or Gradient */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ 
                    background: (tribe.bannerUrl || tribe.image || tribe.bannerImage) 
                      ? `url('${tribe.bannerUrl || tribe.image || tribe.bannerImage}') center/cover no-repeat #2a2a2a` 
                      : 'linear-gradient(135deg, #1a2a1a 0%, #000000 100%)'
                  }}
                />
                
                {/* Radial Gradient Overlay for premium look */}
                <div
                  className="absolute inset-0 backdrop-blur-[1px] transition-all duration-300 z-[1]"
                  style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.85) 100%)' }}
                />

                {/* University tribe centered logo */}
                {(tribe as any).tribeType === 'university' && (tribe as any).schoolLogoUrl && (
                  <img
                    src={(tribe as any).schoolLogoUrl}
                    alt=""
                    className="absolute pointer-events-none z-[2]"
                    style={{
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      maxWidth: 90,
                      maxHeight: 90,
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.9))',
                    }}
                  />
                )}

                {/* Bottom gradient so text stays legible over bright logos */}
                {(tribe as any).tribeType === 'university' && (tribe as any).schoolLogoUrl && (
                  <div className="absolute inset-x-0 bottom-0 h-16 z-[3]" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)' }} />
                )}

                <div className="relative z-10 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 
                          className="text-lg text-white font-bold drop-shadow-md"
                          style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000, 1px 1px 3px rgba(0,0,0,0.8)' }}
                        >
                          {tribe.name}
                        </h3>
                      {trendingIds.has(String(tribe.id)) && (
                        <Badge className="bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white border-none">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Trending
                        </Badge>
                      )}
                    </div>
                    <p 
                      className="text-sm text-white/90 mb-2 drop-shadow-sm"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000' }}
                    >
                      {tribe.description}
                    </p>
                    <div 
                      className="flex items-center gap-4 text-xs text-white/70"
                      style={{ textShadow: '1px 0px 0px #000, -1px 0px 0px #000, 0px 1px 0px #000, 0px -1px 0px #000, 1px 1px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000' }}
                    >
                      <div className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(1px 0px 0px #000) drop-shadow(-1px 0px 0px #000) drop-shadow(0px 1px 0px #000) drop-shadow(0px -1px 0px #000)' }} />
                        <span>{formatMemberCount(tribe.memberCount || (Array.isArray(tribe.members) ? tribe.members.length : tribe.members) || 1)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" style={{ filter: 'drop-shadow(1px 0px 0px #000) drop-shadow(-1px 0px 0px #000) drop-shadow(0px 1px 0px #000) drop-shadow(0px -1px 0px #000)' }} />
                        <span>{tribe.posts || tribe.postCount || 0} posts</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs px-3 py-1 rounded-full bg-[#F39C12]/20 dark:bg-[#4478ea]/20 text-[#F39C12] dark:text-[#4478ea] border border-[#F39C12] dark:border-[#4478ea]">
                    {tribe.primaryTag || tribe.category || 'General'}
                  </span>
                  <button className="px-4 py-2 rounded-xl bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white text-sm hover:opacity-90 transition-opacity">
                    {(userProfile?.joinedTribeIds?.includes(tribe.id) || userProfile?.ownedTribeIds?.includes(tribe.id) || tribe.ownerId === userProfile?.id) ? "View Tribe" : "Join Tribe"}
                  </button>
                </div>
                </div>
              </PressableCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
